// Services d'analytics pour Google Analytics 4 et Microsoft Clarity

// Déclaration des types globaux pour TypeScript
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    clarity: (...args: any[]) => void;
  }
}

// Configuration des IDs depuis les variables d'environnement
export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';
export const CLARITY_PROJECT_ID = process.env.REACT_APP_CLARITY_PROJECT_ID || 'XXXXXXXXXX';

// Interface pour les événements personnalisés
interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
}

class AnalyticsService {
  private isInitialized = false;
  private isClarityInitialized = false;

  // Initialisation de Google Analytics 4
  initializeGA4() {
    if (typeof window === 'undefined' || this.isInitialized) return;
    if (GA_MEASUREMENT_ID === 'G-XXXXXXXXXX') return;

    // Chargement du script GA4
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Configuration GA4
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_title: document.title,
      page_location: window.location.href,
    });

    this.isInitialized = true;
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Google Analytics 4 initialisé');
    }
  }

  // Initialisation de Microsoft Clarity
  initializeClarity() {
    if (typeof window === 'undefined' || this.isClarityInitialized) return;
    if (CLARITY_PROJECT_ID === 'XXXXXXXXXX') return;

    // Script Microsoft Clarity
    const clarityScript = `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "${CLARITY_PROJECT_ID}");
    `;

    const script = document.createElement('script');
    script.innerHTML = clarityScript;
    document.head.appendChild(script);

    this.isClarityInitialized = true;
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Microsoft Clarity initialisé');
    }
  }

  // Suivi des pages vues
  trackPageView(pagePath: string, pageTitle: string) {
    if (typeof window === 'undefined' || !this.isInitialized) return;

    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: pagePath,
      page_title: pageTitle,
    });

    if (process.env.NODE_ENV === 'development') {
      console.log(`📊 Page vue trackée: ${pageTitle} (${pagePath})`);
    }
  }

  // Suivi des événements personnalisés
  trackEvent({ action, category, label, value }: AnalyticsEvent) {
    if (typeof window === 'undefined' || !this.isInitialized) return;

    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });

    if (process.env.NODE_ENV === 'development') {
      console.log(`🎯 Événement tracké: ${action} - ${category}${label ? ` - ${label}` : ''}`);
    }
  }

  // Événements spécifiques pour votre application VTC
  trackBookingAttempt(step: string) {
    this.trackEvent({
      action: 'booking_attempt',
      category: 'booking',
      label: step,
    });
  }

  trackBookingCompleted(serviceType: string, price?: number) {
    this.trackEvent({
      action: 'booking_completed',
      category: 'conversion',
      label: serviceType,
      value: price,
    });
  }

  trackQuoteRequest(serviceType: string) {
    this.trackEvent({
      action: 'quote_request',
      category: 'lead',
      label: serviceType,
    });
  }

  trackContactForm(method: string) {
    this.trackEvent({
      action: 'contact_form_submit',
      category: 'lead',
      label: method, // 'email', 'phone', 'whatsapp'
    });
  }

  trackServiceInterest(serviceType: string) {
    this.trackEvent({
      action: 'service_interest',
      category: 'engagement',
      label: serviceType,
    });
  }
}

// Export de l'instance singleton
export const analytics = new AnalyticsService();
